import { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, ArrowLeft, Edit, Trash2, UserCog, Search, Phone, Mail,
  MapPin, Camera, Loader2, Calendar, Briefcase, FileText, Upload,
  X, Heart, GraduationCap, Users, Clock, DollarSign, Eye, TrendingUp
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

// ==================== MASKS ====================
function maskCPF(v) { return v.replace(/\D/g,"").replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d{1,2})$/,"$1-$2").slice(0,14); }
function maskPhone(v) { const d=v.replace(/\D/g,""); if(d.length<=10) return d.replace(/(\d{2})(\d)/,"($1) $2").replace(/(\d{4})(\d)/,"$1-$2"); return d.replace(/(\d{2})(\d)/,"($1) $2").replace(/(\d{5})(\d)/,"$1-$2").slice(0,15); }
function maskCEP(v) { return v.replace(/\D/g,"").replace(/(\d{5})(\d)/,"$1-$2").slice(0,9); }
function maskPIS(v) { return v.replace(/\D/g,"").replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{5})(\d)/,"$1.$2").replace(/(\d{2})(\d{1})$/,"$1-$2").slice(0,14); }
function maskRG(v) { return v.replace(/[^0-9Xx.-]/g,"").slice(0,20); }

function calcAge(dateStr) {
  if (!dateStr) return "";
  const birth = new Date(dateStr); const today = new Date();
  let y = today.getFullYear() - birth.getFullYear();
  let m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) y--;
  if (y < 0) return "";
  if (y === 0) { m = m < 0 ? m + 12 : m; return `${m} ${m === 1 ? "mes" : "meses"}`; }
  return `${y} ${y === 1 ? "ano" : "anos"}`;
}

function DocUpload({ docs, onAdd, onRemove, label }) {
  return (
    <div className="space-y-2">
      {(docs || []).map((d, i) => (
        <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
          <FileText className="h-4 w-4 text-slate-400 shrink-0" />
          <span className="flex-1 truncate">{d.nome}</span>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRemove(i)}><X className="h-3 w-3" /></Button>
        </div>
      ))}
      <label className="flex items-center gap-2 p-2 border-2 border-dashed rounded cursor-pointer hover:border-[#C9A84C] transition-colors text-sm">
        <Upload className="h-4 w-4 text-slate-400" />
        <span className="text-slate-500">{label || "Anexar documento"}</span>
        <input type="file" accept=".pdf,image/*" multiple onChange={onAdd} className="hidden" />
      </label>
    </div>
  );
}

const ESTADOS_CIVIS = [
  { value: "", label: "Selecione..." },
  { value: "solteiro", label: "Solteiro(a)" },
  { value: "casado", label: "Casado(a)" },
  { value: "uniao_estavel", label: "Uniao Estavel" },
  { value: "divorciado", label: "Divorciado(a)" },
  { value: "viuvo", label: "Viuvo(a)" },
];
const ESCOLARIDADES = [
  { value: "", label: "Selecione..." },
  { value: "fundamental_incompleto", label: "Fundamental Incompleto" },
  { value: "fundamental_completo", label: "Fundamental Completo" },
  { value: "medio_incompleto", label: "Medio Incompleto" },
  { value: "medio_completo", label: "Medio Completo" },
  { value: "tecnico", label: "Tecnico" },
  { value: "superior_incompleto", label: "Superior Incompleto" },
  { value: "superior_completo", label: "Superior Completo" },
  { value: "pos_graduacao", label: "Pos-Graduacao" },
  { value: "mestrado", label: "Mestrado" },
  { value: "doutorado", label: "Doutorado" },
];
const TIPOS_EXAME = [
  { value: "admissional", label: "Admissional" },
  { value: "periodico", label: "Periodico" },
  { value: "retorno_trabalho", label: "Retorno ao Trabalho" },
  { value: "mudanca_funcao", label: "Mudanca de Funcao" },
  { value: "demissional", label: "Demissional" },
];
const CNH_CATEGORIAS = [
  { value: "", label: "Selecione..." },
  { value: "A", label: "A" }, { value: "B", label: "B" }, { value: "AB", label: "AB" },
  { value: "C", label: "C" }, { value: "D", label: "D" }, { value: "E", label: "E" },
];
const DIAS_SEMANA = [
  { value: "segunda", label: "Segunda-feira" },
  { value: "terca", label: "Terca-feira" },
  { value: "quarta", label: "Quarta-feira" },
  { value: "quinta", label: "Quinta-feira" },
  { value: "sexta", label: "Sexta-feira" },
  { value: "sabado", label: "Sabado" },
  { value: "domingo", label: "Domingo" },
];

const EMPTY_FORM = {
  nome: "", cpf: "", data_nascimento: "", estado_civil: "",
  foto: "", data_admissao: "", cargo_id: "", cargo_nome: "",
  cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "",
  comprovante_residencia: [],
  telefone_contato: "", email: "",
  contatos_emergencia: [],
  rg_numero: "", rg_data_emissao: "", rg_orgao_emissor: "", rg_docs: [],
  cnh_numero: "", cnh_categoria: "", cnh_validade: "", cnh_docs: [],
  titulo_eleitor_inscricao: "", titulo_eleitor_zona: "", titulo_eleitor_secao: "", titulo_eleitor_docs: [],
  pis: "", pis_docs: [],
  certificado_reservista: "", certificado_reservista_docs: [],
  estado_civil_docs: [],
  filiacao_pai: "", filiacao_mae: "",
  filhos: [],
  grau_escolaridade: "", escolaridade_docs: [], cursos: [],
  carga_horaria_semanal: "", horario_entrada: "", horario_saida_almoco: "",
  horario_retorno_almoco: "", horario_saida: "", dias_folga: [],
  salario_inicial: "", beneficios: [], vale_transporte_rotas: [],
  ferias_periodo_aquisitivo_inicio: "", ferias_periodo_aquisitivo_fim: "", ferias_programacao: [],
  exames_saude: [],
  historico_salarial: [], historico_promocoes: [],
};

// ==================== LIST ====================
function FuncionariosList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchItems(); }, []);
  const fetchItems = async () => {
    try { const res = await axios.get(`${API}/funcionarios`, { headers }); setItems(res.data); }
    catch { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  };
  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/funcionarios/${id}`, { headers }); toast.success("Excluido"); fetchItems(); }
    catch { toast.error("Erro"); }
  };

  const filtered = items.filter(i =>
    (i.nome || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (i.cpf || "").includes(searchTerm) ||
    (i.cargo_nome || "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const getInitials = (n) => n?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "??";

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#0A1628]" /></div>;

  return (
    <div className="space-y-4" data-testid="funcionarios-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-[#0A1628]">Funcionarios LuvCorp</h1>
        <Link to="/cadastros/funcionarios/new">
          <Button className="bg-[#0A1628] hover:bg-[#0A1628]/90" data-testid="btn-novo-func"><Plus className="h-4 w-4 mr-2" />Novo Funcionario</Button>
        </Link>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input placeholder="Buscar por nome, CPF ou cargo..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-input" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(item => (
          <Card key={item.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Avatar className="h-14 w-14 shrink-0">
                  {item.foto ? <AvatarImage src={item.foto} /> : null}
                  <AvatarFallback className="bg-[#0A1628] text-white">{getInitials(item.nome)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#0A1628] truncate">{item.nome}</p>
                  {item.cargo_nome && <Badge variant="outline" className="mt-0.5 text-xs">{item.cargo_nome}</Badge>}
                  <p className="text-xs text-slate-500 mt-1 font-mono">{item.cpf}</p>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-xs text-slate-500">
                {item.telefone_contato && <p className="flex items-center gap-1"><Phone className="h-3 w-3" />{item.telefone_contato}</p>}
                {item.email && <p className="flex items-center gap-1"><Mail className="h-3 w-3" />{item.email}</p>}
                {item.data_admissao && <p className="flex items-center gap-1"><Calendar className="h-3 w-3" />Admissao: {new Date(item.data_admissao).toLocaleDateString("pt-BR")}</p>}
              </div>
              <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                <Link to={`/cadastros/funcionarios/${item.id}`}><Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button></Link>
                <Link to={`/cadastros/funcionarios/${item.id}/edit`}><Button variant="outline" size="sm"><Edit className="h-4 w-4 mr-1" />Editar</Button></Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-red-500 border-red-200"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Excluir?</AlertDialogTitle><AlertDialogDescription>Esta acao nao pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-red-600">Excluir</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <Card className="border-0 shadow-sm col-span-full"><CardContent className="p-12 text-center"><UserCog className="h-12 w-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">Nenhum funcionario encontrado</p></CardContent></Card>}
      </div>
    </div>
  );
}

// ==================== FORM (5 TABS) ====================
function FuncionarioForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [cpfLooking, setCpfLooking] = useState(false);
  const [cepLooking, setCepLooking] = useState(false);
  const [activeTab, setActiveTab] = useState("pessoais");
  const [cargos, setCargos] = useState([]);
  const [showCargoDialog, setShowCargoDialog] = useState(false);
  const [newCargo, setNewCargo] = useState({ nome: "", descricao: "", nivel: 1, salario_base: "" });
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [promoData, setPromoData] = useState({ novo_cargo_id: "", novo_cargo_nome: "", novo_salario: "", motivo: "" });
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  useEffect(() => {
    axios.get(`${API}/cargos`, { headers }).then(r => setCargos(r.data)).catch(() => {});
    if (isEdit) {
      axios.get(`${API}/funcionarios/${id}`, { headers })
        .then(r => {
          const d = r.data;
          const merged = { ...EMPTY_FORM };
          Object.keys(merged).forEach(k => { if (d[k] !== undefined && d[k] !== null) merged[k] = d[k]; });
          // Ensure arrays
          ["contatos_emergencia","filhos","cursos","beneficios","vale_transporte_rotas","ferias_programacao","exames_saude","historico_salarial","historico_promocoes","comprovante_residencia","estado_civil_docs","rg_docs","cnh_docs","titulo_eleitor_docs","pis_docs","certificado_reservista_docs","escolaridade_docs","dias_folga"].forEach(k => { if (!Array.isArray(merged[k])) merged[k] = []; });
          if (merged.salario_inicial === null) merged.salario_inicial = "";
          if (merged.carga_horaria_semanal === null) merged.carga_horaria_semanal = "";
          setForm(merged);
          setLoading(false);
        })
        .catch(() => { toast.error("Erro ao carregar"); navigate("/cadastros/funcionarios"); });
    }
  }, [id]);

  const lookupCPF = async () => {
    const cpf = (form.cpf || "").replace(/\D/g, "");
    if (cpf.length !== 11) { toast.error("CPF deve ter 11 digitos"); return; }
    setCpfLooking(true);
    try {
      const res = await axios.get(`${API}/lookup/cpf/${cpf}`, { headers });
      const d = res.data;
      if (d.fonte === "cadastro_interno" && d.nome) {
        setForm(p => ({ ...p, nome: d.nome || p.nome, email: d.email || p.email, telefone_contato: d.telefone || p.telefone_contato, cep: d.cep || p.cep, endereco: d.endereco || p.endereco, numero: d.numero || p.numero, bairro: d.bairro || p.bairro, cidade: d.cidade || p.cidade, estado: d.estado || p.estado }));
        toast.success("Dados encontrados no cadastro interno!");
      } else {
        toast.info("CPF valido. Sem dados no cadastro interno.");
      }
    } catch (err) { toast.error(extractErrorMessage(err, "CPF invalido")); }
    finally { setCpfLooking(false); }
  };

  const lookupCEP = async () => {
    const cep = (form.cep || "").replace(/\D/g, "");
    if (cep.length !== 8) { toast.error("CEP invalido"); return; }
    setCepLooking(true);
    try {
      const res = await axios.get(`${API}/lookup/cep/${cep}`, { headers });
      const d = res.data;
      setForm(p => ({ ...p, endereco: d.endereco || p.endereco, bairro: d.bairro || p.bairro, cidade: d.cidade || p.cidade, estado: d.estado || p.estado }));
      toast.success("Endereco preenchido!");
    } catch { toast.error("CEP nao encontrado"); }
    finally { setCepLooking(false); }
  };

  const handleCargoChange = (cargoId) => {
    const c = cargos.find(x => x.id === cargoId);
    setForm(p => ({
      ...p,
      cargo_id: cargoId,
      cargo_nome: c?.nome || "",
      salario_inicial: c?.salario_base ? c.salario_base : p.salario_inicial
    }));
    if (c?.salario_base) toast.info(`Salario base do cargo: R$ ${Number(c.salario_base).toFixed(2)}`);
  };

  const handleCreateCargo = async () => {
    if (!newCargo.nome.trim()) return;
    try {
      const payload = { ...newCargo, salario_base: newCargo.salario_base === "" ? null : parseFloat(newCargo.salario_base) };
      const res = await axios.post(`${API}/cargos`, payload, { headers });
      await axios.get(`${API}/cargos`, { headers }).then(r => setCargos(r.data));
      handleCargoChange(res.data.id);
      setShowCargoDialog(false);
      setNewCargo({ nome: "", descricao: "", nivel: 1, salario_base: "" });
      toast.success("Cargo criado!");
    } catch (err) { toast.error(extractErrorMessage(err, "Erro")); }
  };

  const handlePromotion = () => {
    const now = new Date().toISOString().split("T")[0];
    const oldCargo = form.cargo_nome;
    const oldSalario = form.salario_inicial;
    const newEntry = {
      data: now,
      cargo_anterior: oldCargo,
      salario_anterior: oldSalario,
      cargo_novo: promoData.novo_cargo_nome,
      salario_novo: parseFloat(promoData.novo_salario) || null,
      motivo: promoData.motivo,
    };
    const salaryEntry = {
      data: now,
      valor_anterior: oldSalario,
      valor_novo: parseFloat(promoData.novo_salario) || null,
      motivo: `Promocao: ${promoData.motivo || ""}`,
    };
    setForm(p => ({
      ...p,
      cargo_id: promoData.novo_cargo_id,
      cargo_nome: promoData.novo_cargo_nome,
      salario_inicial: parseFloat(promoData.novo_salario) || p.salario_inicial,
      historico_promocoes: [...(p.historico_promocoes || []), newEntry],
      historico_salarial: [...(p.historico_salarial || []), salaryEntry],
    }));
    setShowPromoDialog(false);
    setPromoData({ novo_cargo_id: "", novo_cargo_nome: "", novo_salario: "", motivo: "" });
    toast.success("Promocao registrada! Salve o formulario para confirmar.");
  };

  const handleDocUpload = (e, field) => {
    Array.from(e.target.files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setForm(p => ({ ...p, [field]: [...(p[field] || []), { nome: file.name, arquivo_base64: ev.target.result }] }));
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const removeDoc = (field, i) => setForm(p => ({ ...p, [field]: p[field].filter((_, idx) => idx !== i) }));

  const handleNestedDocUpload = (e, listField, index, docField) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(p => { const list = [...(p[listField] || [])]; list[index] = { ...list[index], [docField]: [...(list[index][docField] || []), { nome: file.name, arquivo_base64: ev.target.result }] }; return { ...p, [listField]: list }; });
    };
    reader.readAsDataURL(file); e.target.value = "";
  };
  const removeNestedDoc = (listField, index, docField, docIdx) => {
    setForm(p => { const list = [...(p[listField] || [])]; list[index] = { ...list[index], [docField]: list[index][docField].filter((_, i) => i !== docIdx) }; return { ...p, [listField]: list }; });
  };

  const addToList = (field, template) => setForm(p => ({ ...p, [field]: [...(p[field] || []), template] }));
  const updateListItem = (field, i, key, val) => setForm(p => { const list = [...(p[field] || [])]; list[i] = { ...list[i], [key]: val }; return { ...p, [field]: list }; });
  const removeFromList = (field, i) => setForm(p => ({ ...p, [field]: p[field].filter((_, idx) => idx !== i) }));

  const toggleDiaFolga = (dia) => {
    setForm(p => {
      const current = p.dias_folga || [];
      return { ...p, dias_folga: current.includes(dia) ? current.filter(d => d !== dia) : [...current, dia] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cpf) { toast.error("Nome e CPF obrigatorios"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.salario_inicial === "") payload.salario_inicial = null;
      if (payload.carga_horaria_semanal === "") payload.carga_horaria_semanal = null;
      if (isEdit) { await axios.put(`${API}/funcionarios/${id}`, payload, { headers }); toast.success("Atualizado!"); }
      else { await axios.post(`${API}/funcionarios`, payload, { headers }); toast.success("Cadastrado!"); }
      navigate("/cadastros/funcionarios");
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao salvar")); }
    finally { setSaving(false); }
  };

  const handlePhotoChange = (e) => { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = (ev) => set("foto", ev.target.result); r.readAsDataURL(file); };
  const getInitials = (n) => n?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "??";

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="funcionario-form">
      <div className="flex items-center gap-3">
        <Link to="/cadastros/funcionarios"><Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button></Link>
        <h1 className="text-xl font-heading font-bold text-[#0A1628]">{isEdit ? "Editar Funcionario" : "Novo Funcionario"}</h1>
        {isEdit && (
          <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={() => setShowPromoDialog(true)} data-testid="btn-promocao">
            <TrendingUp className="h-4 w-4 mr-1" />Registrar Promocao
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5 bg-[#0A1628] h-auto">
            {[
              ["pessoais", "Dados Pessoais", <UserCog key="i1" className="h-4 w-4" />],
              ["documentos", "Documentos", <FileText key="i2" className="h-4 w-4" />],
              ["familia", "Familia", <Users key="i3" className="h-4 w-4" />],
              ["trabalho", "Trabalho", <Briefcase key="i4" className="h-4 w-4" />],
              ["saude", "Saude", <Heart key="i5" className="h-4 w-4" />],
            ].map(([val, label, icon]) => (
              <TabsTrigger key={val} value={val} className="text-white data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#0A1628] text-[10px] md:text-xs py-2 gap-1">
                <span className="hidden md:inline">{icon}</span>{label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* === TAB 1: DADOS PESSOAIS + ENDERECO + CONTATOS === */}
          <TabsContent value="pessoais" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Dados Pessoais</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                    <Avatar className="h-24 w-24 cursor-pointer hover:opacity-80" onClick={() => fileInputRef.current?.click()}>
                      {form.foto ? <AvatarImage src={form.foto} /> : null}
                      <AvatarFallback className="bg-[#0A1628] text-white text-2xl">{getInitials(form.nome)}</AvatarFallback>
                    </Avatar>
                    <div className="absolute bottom-0 right-0 h-8 w-8 bg-[#C9A84C] rounded-full flex items-center justify-center cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                      <Camera className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Nome Completo *</Label><Input value={form.nome} onChange={e => set("nome", e.target.value)} data-testid="input-nome" /></div>
                  <div className="space-y-2">
                    <Label>Data de Nascimento</Label>
                    <Input type="date" value={form.data_nascimento} onChange={e => set("data_nascimento", e.target.value)} data-testid="input-nascimento" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Estado Civil</Label><NativeSelect value={form.estado_civil} onValueChange={v => set("estado_civil", v)} options={ESTADOS_CIVIS} data-testid="select-estado-civil" /></div>
                  <div className="space-y-2">
                    <Label>Cargo</Label>
                    <div className="flex gap-2">
                      <NativeSelect value={form.cargo_id || ""} onValueChange={handleCargoChange} placeholder="Selecione..." options={[{ value: "", label: "Sem cargo" }, ...cargos.map(c => ({ value: c.id, label: `${c.nome} (Nv.${c.nivel})${c.salario_base ? ` - R$${Number(c.salario_base).toFixed(2)}` : ""}` }))]} className="flex-1" data-testid="select-cargo" />
                      <Button type="button" variant="outline" onClick={() => setShowCargoDialog(true)} title="Novo cargo"><Plus className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
                <div className="space-y-2"><Label>Data de Admissao</Label><Input type="date" value={form.data_admissao} onChange={e => set("data_admissao", e.target.value)} data-testid="input-admissao" /></div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Endereco</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>CEP</Label>
                    <div className="flex gap-2">
                      <Input value={form.cep} onChange={e => set("cep", maskCEP(e.target.value))} placeholder="00000-000" data-testid="input-cep" />
                      <Button type="button" variant="outline" size="sm" onClick={lookupCEP} disabled={cepLooking} className="shrink-0">{cepLooking ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}</Button>
                    </div>
                  </div>
                  <div className="space-y-2 md:col-span-2"><Label>Logradouro</Label><Input value={form.endereco} onChange={e => set("endereco", e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2"><Label>Numero</Label><Input value={form.numero} onChange={e => set("numero", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Complemento</Label><Input value={form.complemento} onChange={e => set("complemento", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Bairro</Label><Input value={form.bairro} onChange={e => set("bairro", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Cidade / UF</Label><div className="flex gap-2"><Input value={form.cidade} onChange={e => set("cidade", e.target.value)} className="flex-1" /><Input value={form.estado} onChange={e => set("estado", e.target.value)} className="w-16" maxLength={2} /></div></div>
                </div>
                <div className="space-y-2"><Label>Comprovante de Residencia</Label><DocUpload docs={form.comprovante_residencia} onAdd={e => handleDocUpload(e, "comprovante_residencia")} onRemove={i => removeDoc("comprovante_residencia", i)} label="Anexar comprovante" /></div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Contatos</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Telefone / WhatsApp</Label><Input value={form.telefone_contato} onChange={e => set("telefone_contato", maskPhone(e.target.value))} placeholder="(00) 00000-0000" data-testid="input-telefone" /></div>
                  <div className="space-y-2"><Label>E-mail</Label><Input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="input-email" /></div>
                </div>
                <Separator />
                <div className="flex items-center justify-between"><p className="text-sm font-medium text-[#0A1628]">Contatos para Emergencia</p><Button type="button" variant="outline" size="sm" onClick={() => addToList("contatos_emergencia", { nome: "", telefone: "" })}><Plus className="h-3 w-3 mr-1" />Adicionar</Button></div>
                {(form.contatos_emergencia || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 p-3 bg-slate-50 rounded" data-testid={`emergencia-${i}`}>
                    <Input value={c.nome} onChange={e => updateListItem("contatos_emergencia", i, "nome", e.target.value)} placeholder="Nome" />
                    <Input value={c.telefone} onChange={e => updateListItem("contatos_emergencia", i, "telefone", maskPhone(e.target.value))} placeholder="(00) 00000-0000" />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeFromList("contatos_emergencia", i)}><X className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB 2: DOCUMENTOS (CPF, RG, CNH, Titulo, PIS, Reservista, Estado Civil) === */}
          <TabsContent value="documentos" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">CPF</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Numero do CPF *</Label>
                  <div className="flex gap-2">
                    <Input value={form.cpf} onChange={e => set("cpf", maskCPF(e.target.value))} placeholder="000.000.000-00" data-testid="input-cpf" />
                    <Button type="button" variant="outline" onClick={lookupCPF} disabled={cpfLooking} className="shrink-0" data-testid="btn-lookup-cpf">
                      {cpfLooking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      <span className="ml-1 hidden md:inline">Consultar</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">RG / Carteira de Identidade</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Numero do RG</Label><Input value={form.rg_numero} onChange={e => set("rg_numero", maskRG(e.target.value))} placeholder="00.000.000-0" data-testid="input-rg" /></div>
                  <div className="space-y-2"><Label>Data de Emissao</Label><Input type="date" value={form.rg_data_emissao} onChange={e => set("rg_data_emissao", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Orgao Emissor</Label><Input value={form.rg_orgao_emissor} onChange={e => set("rg_orgao_emissor", e.target.value)} placeholder="SSP/SP" /></div>
                </div>
                <DocUpload docs={form.rg_docs} onAdd={e => handleDocUpload(e, "rg_docs")} onRemove={i => removeDoc("rg_docs", i)} label="Anexar copia do RG" />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">CNH</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Numero</Label><Input value={form.cnh_numero} onChange={e => set("cnh_numero", e.target.value.replace(/\D/g, "").slice(0, 11))} data-testid="input-cnh" /></div>
                  <div className="space-y-2"><Label>Categoria</Label><NativeSelect value={form.cnh_categoria} onValueChange={v => set("cnh_categoria", v)} options={CNH_CATEGORIAS} /></div>
                  <div className="space-y-2"><Label>Validade</Label><Input type="date" value={form.cnh_validade} onChange={e => set("cnh_validade", e.target.value)} /></div>
                </div>
                <DocUpload docs={form.cnh_docs} onAdd={e => handleDocUpload(e, "cnh_docs")} onRemove={i => removeDoc("cnh_docs", i)} label="Anexar CNH" />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Titulo de Eleitor</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Inscricao</Label><Input value={form.titulo_eleitor_inscricao} onChange={e => set("titulo_eleitor_inscricao", e.target.value.replace(/\D/g, "").slice(0, 12))} placeholder="000000000000" data-testid="input-titulo-inscricao" /></div>
                  <div className="space-y-2"><Label>Zona</Label><Input value={form.titulo_eleitor_zona} onChange={e => set("titulo_eleitor_zona", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="0000" /></div>
                  <div className="space-y-2"><Label>Secao</Label><Input value={form.titulo_eleitor_secao} onChange={e => set("titulo_eleitor_secao", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="0000" /></div>
                </div>
                <DocUpload docs={form.titulo_eleitor_docs} onAdd={e => handleDocUpload(e, "titulo_eleitor_docs")} onRemove={i => removeDoc("titulo_eleitor_docs", i)} label="Anexar titulo" />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">PIS / PASEP</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Numero</Label><Input value={form.pis} onChange={e => set("pis", maskPIS(e.target.value))} placeholder="000.00000.00-0" data-testid="input-pis" /></div>
                <DocUpload docs={form.pis_docs} onAdd={e => handleDocUpload(e, "pis_docs")} onRemove={i => removeDoc("pis_docs", i)} label="Anexar PIS" />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Certificado de Reservista</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Numero</Label><Input value={form.certificado_reservista} onChange={e => set("certificado_reservista", e.target.value.replace(/\D/g, "").slice(0, 12))} /></div>
                <DocUpload docs={form.certificado_reservista_docs} onAdd={e => handleDocUpload(e, "certificado_reservista_docs")} onRemove={i => removeDoc("certificado_reservista_docs", i)} label="Anexar certificado" />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Documentos do Estado Civil</CardTitle></CardHeader>
              <CardContent><DocUpload docs={form.estado_civil_docs} onAdd={e => handleDocUpload(e, "estado_civil_docs")} onRemove={i => removeDoc("estado_civil_docs", i)} label="Anexar certidao de casamento, uniao estavel, etc." /></CardContent>
            </Card>
          </TabsContent>

          {/* === TAB 3: FAMILIA & ESCOLARIDADE === */}
          <TabsContent value="familia" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Filiacao</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Nome da Mae</Label><Input value={form.filiacao_mae} onChange={e => set("filiacao_mae", e.target.value)} placeholder="Nome completo da mae" data-testid="input-filiacao-mae" /></div>
                  <div className="space-y-2"><Label>Nome do Pai</Label><Input value={form.filiacao_pai} onChange={e => set("filiacao_pai", e.target.value)} placeholder="Nome completo do pai" data-testid="input-filiacao-pai" /></div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg text-[#0A1628]">Filhos / Dependentes</CardTitle><Button type="button" variant="outline" size="sm" onClick={() => addToList("filhos", { nome: "", sexo: "", data_nascimento: "", certidao_nascimento: [], cartao_vacina: [] })} data-testid="btn-add-filho"><Plus className="h-3 w-3 mr-1" />Adicionar</Button></div></CardHeader>
              <CardContent className="space-y-4">
                {(form.filhos || []).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nenhum dependente</p>}
                {(form.filhos || []).map((f, i) => (
                  <div key={i} className="p-4 border rounded-lg space-y-3" data-testid={`filho-${i}`}>
                    <div className="flex items-center justify-between"><p className="text-sm font-medium text-[#0A1628]">Dependente {i + 1}</p><Button type="button" variant="ghost" size="icon" onClick={() => removeFromList("filhos", i)}><X className="h-4 w-4 text-red-500" /></Button></div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="space-y-1 md:col-span-2"><Label className="text-xs">Nome</Label><Input value={f.nome} onChange={e => updateListItem("filhos", i, "nome", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">Sexo</Label><NativeSelect value={f.sexo} onValueChange={v => updateListItem("filhos", i, "sexo", v)} options={[{ value: "", label: "Selecione" }, { value: "masculino", label: "Masculino" }, { value: "feminino", label: "Feminino" }]} /></div>
                      <div className="space-y-1"><Label className="text-xs">Nascimento</Label><Input type="date" value={f.data_nascimento} onChange={e => updateListItem("filhos", i, "data_nascimento", e.target.value)} /></div>
                    </div>
                    {f.data_nascimento && <p className="text-xs text-[#C9A84C] font-medium">Idade: {calcAge(f.data_nascimento)}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs">Certidao de Nascimento</Label><DocUpload docs={f.certidao_nascimento || []} onAdd={e => handleNestedDocUpload(e, "filhos", i, "certidao_nascimento")} onRemove={di => removeNestedDoc("filhos", i, "certidao_nascimento", di)} label="Anexar certidao" /></div>
                      <div className="space-y-1"><Label className="text-xs">Cartao de Vacina</Label><DocUpload docs={f.cartao_vacina || []} onAdd={e => handleNestedDocUpload(e, "filhos", i, "cartao_vacina")} onRemove={di => removeNestedDoc("filhos", i, "cartao_vacina", di)} label="Anexar cartao" /></div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Escolaridade</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Grau de Escolaridade</Label><NativeSelect value={form.grau_escolaridade} onValueChange={v => set("grau_escolaridade", v)} options={ESCOLARIDADES} data-testid="select-escolaridade" /></div>
                <DocUpload docs={form.escolaridade_docs} onAdd={e => handleDocUpload(e, "escolaridade_docs")} onRemove={i => removeDoc("escolaridade_docs", i)} label="Anexar diploma/certificado" />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg text-[#0A1628]">Cursos e Capacitacoes</CardTitle><Button type="button" variant="outline" size="sm" onClick={() => addToList("cursos", { nome: "", instituicao: "", data_conclusao: "", carga_horaria: "", docs: [] })} data-testid="btn-add-curso"><Plus className="h-3 w-3 mr-1" />Adicionar</Button></div></CardHeader>
              <CardContent className="space-y-4">
                {(form.cursos || []).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nenhum curso</p>}
                {(form.cursos || []).map((c, i) => (
                  <div key={i} className="p-4 border rounded-lg space-y-3" data-testid={`curso-${i}`}>
                    <div className="flex items-center justify-between"><p className="text-sm font-medium">Curso {i + 1}</p><Button type="button" variant="ghost" size="icon" onClick={() => removeFromList("cursos", i)}><X className="h-4 w-4 text-red-500" /></Button></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs">Nome</Label><Input value={c.nome} onChange={e => updateListItem("cursos", i, "nome", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">Instituicao</Label><Input value={c.instituicao} onChange={e => updateListItem("cursos", i, "instituicao", e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs">Data Conclusao</Label><Input type="date" value={c.data_conclusao} onChange={e => updateListItem("cursos", i, "data_conclusao", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">Carga Horaria (h)</Label><Input type="number" value={c.carga_horaria} onChange={e => updateListItem("cursos", i, "carga_horaria", e.target.value)} /></div>
                    </div>
                    <DocUpload docs={c.docs || []} onAdd={e => handleNestedDocUpload(e, "cursos", i, "docs")} onRemove={di => removeNestedDoc("cursos", i, "docs", di)} label="Anexar certificado" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB 4: TRABALHO & BENEFICIOS === */}
          <TabsContent value="trabalho" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628] flex items-center gap-2"><Clock className="h-5 w-5" />Carga Horaria</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Carga Horaria Semanal (h)</Label><Input type="number" value={form.carga_horaria_semanal} onChange={e => set("carga_horaria_semanal", e.target.value)} placeholder="44" data-testid="input-carga-horaria" /></div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2"><Label>Entrada</Label><Input type="time" value={form.horario_entrada} onChange={e => set("horario_entrada", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Saida Almoco</Label><Input type="time" value={form.horario_saida_almoco} onChange={e => set("horario_saida_almoco", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Retorno Almoco</Label><Input type="time" value={form.horario_retorno_almoco} onChange={e => set("horario_retorno_almoco", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Saida</Label><Input type="time" value={form.horario_saida} onChange={e => set("horario_saida", e.target.value)} /></div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label>Dias de Folga</Label>
                  <div className="flex flex-wrap gap-2">
                    {DIAS_SEMANA.map(d => (
                      <label key={d.value} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-pointer text-sm transition-colors ${(form.dias_folga || []).includes(d.value) ? "bg-[#C9A84C] text-white border-[#C9A84C]" : "bg-white hover:bg-slate-50"}`}>
                        <Checkbox checked={(form.dias_folga || []).includes(d.value)} onCheckedChange={() => toggleDiaFolga(d.value)} className="hidden" />
                        {d.label}
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628] flex items-center gap-2"><DollarSign className="h-5 w-5" />Salario e Beneficios</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Salario Atual (R$)</Label><Input type="number" step="0.01" value={form.salario_inicial} onChange={e => set("salario_inicial", e.target.value)} placeholder="Preenchido automaticamente pelo cargo" data-testid="input-salario" /><p className="text-xs text-slate-500">O salario e preenchido automaticamente ao selecionar o cargo na aba Dados Pessoais</p></div>
                <Separator />
                <div className="flex items-center justify-between"><p className="text-sm font-medium text-[#0A1628]">Beneficios</p><Button type="button" variant="outline" size="sm" onClick={() => addToList("beneficios", { tipo: "", valor: "", detalhes: "" })}><Plus className="h-3 w-3 mr-1" />Adicionar</Button></div>
                {(form.beneficios || []).map((b, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_100px_1fr_auto] gap-2 p-3 bg-slate-50 rounded" data-testid={`beneficio-${i}`}>
                    <NativeSelect value={b.tipo} onValueChange={v => updateListItem("beneficios", i, "tipo", v)} options={[{ value: "", label: "Tipo..." }, { value: "vale_alimentacao", label: "Vale Alimentacao" }, { value: "vale_refeicao", label: "Vale Refeicao" }, { value: "vale_transporte", label: "Vale Transporte" }, { value: "plano_saude", label: "Plano de Saude" }, { value: "plano_odontologico", label: "Plano Odontologico" }, { value: "seguro_vida", label: "Seguro de Vida" }, { value: "outro", label: "Outro" }]} />
                    <Input type="number" step="0.01" value={b.valor} onChange={e => updateListItem("beneficios", i, "valor", e.target.value)} placeholder="R$" />
                    <Input value={b.detalhes} onChange={e => updateListItem("beneficios", i, "detalhes", e.target.value)} placeholder="Detalhes" />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeFromList("beneficios", i)}><X className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Separator />
                <div className="flex items-center justify-between"><p className="text-sm font-medium text-[#0A1628]">Rotas Vale Transporte</p><Button type="button" variant="outline" size="sm" onClick={() => addToList("vale_transporte_rotas", { descricao_ida: "", meio_ida: "", valor_ida: "", descricao_volta: "", meio_volta: "", valor_volta: "" })}><Plus className="h-3 w-3 mr-1" />Rota</Button></div>
                {(form.vale_transporte_rotas || []).map((r, i) => (
                  <div key={i} className="p-3 border rounded-lg space-y-2" data-testid={`rota-vt-${i}`}>
                    <div className="flex items-center justify-between"><p className="text-xs font-medium text-slate-500">Rota {i + 1}</p><Button type="button" variant="ghost" size="icon" onClick={() => removeFromList("vale_transporte_rotas", i)}><X className="h-4 w-4 text-red-500" /></Button></div>
                    <p className="text-xs text-[#C9A84C] font-medium">Ida</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Input value={r.descricao_ida} onChange={e => updateListItem("vale_transporte_rotas", i, "descricao_ida", e.target.value)} placeholder="Ex: Casa - Metro" />
                      <NativeSelect value={r.meio_ida} onValueChange={v => updateListItem("vale_transporte_rotas", i, "meio_ida", v)} options={[{ value: "", label: "Meio..." }, { value: "onibus", label: "Onibus" }, { value: "metro", label: "Metro" }, { value: "trem", label: "Trem" }, { value: "van", label: "Van" }, { value: "barca", label: "Barca" }]} />
                      <Input type="number" step="0.01" value={r.valor_ida} onChange={e => updateListItem("vale_transporte_rotas", i, "valor_ida", e.target.value)} placeholder="R$" />
                    </div>
                    <p className="text-xs text-[#C9A84C] font-medium">Volta</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Input value={r.descricao_volta} onChange={e => updateListItem("vale_transporte_rotas", i, "descricao_volta", e.target.value)} placeholder="Ex: Metro - Casa" />
                      <NativeSelect value={r.meio_volta} onValueChange={v => updateListItem("vale_transporte_rotas", i, "meio_volta", v)} options={[{ value: "", label: "Meio..." }, { value: "onibus", label: "Onibus" }, { value: "metro", label: "Metro" }, { value: "trem", label: "Trem" }, { value: "van", label: "Van" }, { value: "barca", label: "Barca" }]} />
                      <Input type="number" step="0.01" value={r.valor_volta} onChange={e => updateListItem("vale_transporte_rotas", i, "valor_volta", e.target.value)} placeholder="R$" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628] flex items-center gap-2"><Calendar className="h-5 w-5" />Ferias</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Periodo Aquisitivo - Inicio</Label><Input type="date" value={form.ferias_periodo_aquisitivo_inicio} onChange={e => set("ferias_periodo_aquisitivo_inicio", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Periodo Aquisitivo - Fim</Label><Input type="date" value={form.ferias_periodo_aquisitivo_fim} onChange={e => set("ferias_periodo_aquisitivo_fim", e.target.value)} /></div>
                </div>
                <Separator />
                <div className="flex items-center justify-between"><p className="text-sm font-medium">Programacao</p><Button type="button" variant="outline" size="sm" onClick={() => addToList("ferias_programacao", { data_inicio: "", data_fim: "", observacao: "" })}><Plus className="h-3 w-3 mr-1" />Periodo</Button></div>
                {(form.ferias_programacao || []).map((f, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 p-3 bg-slate-50 rounded">
                    <Input type="date" value={f.data_inicio} onChange={e => updateListItem("ferias_programacao", i, "data_inicio", e.target.value)} />
                    <Input type="date" value={f.data_fim} onChange={e => updateListItem("ferias_programacao", i, "data_fim", e.target.value)} />
                    <Input value={f.observacao} onChange={e => updateListItem("ferias_programacao", i, "observacao", e.target.value)} placeholder="Obs" />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeFromList("ferias_programacao", i)}><X className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Historico Salarial e Promocoes */}
            {((form.historico_salarial || []).length > 0 || (form.historico_promocoes || []).length > 0) && (
              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-lg text-[#0A1628] flex items-center gap-2"><TrendingUp className="h-5 w-5" />Historico de Atualizacoes</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {(form.historico_promocoes || []).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-[#0A1628]">Promocoes</p>
                      {form.historico_promocoes.map((p, i) => (
                        <div key={i} className="p-3 bg-emerald-50 rounded text-sm space-y-1">
                          <p className="font-medium">{p.data ? new Date(p.data).toLocaleDateString("pt-BR") : ""} - {p.motivo}</p>
                          <p className="text-slate-500">{p.cargo_anterior} {p.salario_anterior ? `(R$ ${Number(p.salario_anterior).toFixed(2)})` : ""} → {p.cargo_novo} {p.salario_novo ? `(R$ ${Number(p.salario_novo).toFixed(2)})` : ""}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {(form.historico_salarial || []).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-[#0A1628]">Atualizacoes Salariais</p>
                      {form.historico_salarial.map((s, i) => (
                        <div key={i} className="p-2 bg-slate-50 rounded text-sm flex items-center justify-between">
                          <span>{s.data ? new Date(s.data).toLocaleDateString("pt-BR") : ""} - {s.motivo}</span>
                          <span className="font-mono">{s.valor_anterior ? `R$ ${Number(s.valor_anterior).toFixed(2)}` : "-"} → {s.valor_novo ? `R$ ${Number(s.valor_novo).toFixed(2)}` : "-"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* === TAB 5: SAUDE OCUPACIONAL === */}
          <TabsContent value="saude" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg text-[#0A1628] flex items-center gap-2"><Heart className="h-5 w-5 text-red-500" />Exames de Saude Ocupacional</CardTitle><Button type="button" variant="outline" size="sm" onClick={() => addToList("exames_saude", { tipo: "", data_exame: "", resultado: "", proximo_exame: "", docs: [] })} data-testid="btn-add-exame"><Plus className="h-3 w-3 mr-1" />Exame</Button></div></CardHeader>
              <CardContent className="space-y-4">
                {(form.exames_saude || []).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nenhum exame</p>}
                {(form.exames_saude || []).map((ex, i) => (
                  <div key={i} className="p-4 border rounded-lg space-y-3" data-testid={`exame-${i}`}>
                    <div className="flex items-center justify-between"><p className="text-sm font-medium">Exame {i + 1}</p><Button type="button" variant="ghost" size="icon" onClick={() => removeFromList("exames_saude", i)}><X className="h-4 w-4 text-red-500" /></Button></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs">Tipo</Label><NativeSelect value={ex.tipo} onValueChange={v => updateListItem("exames_saude", i, "tipo", v)} options={[{ value: "", label: "Selecione..." }, ...TIPOS_EXAME]} /></div>
                      <div className="space-y-1"><Label className="text-xs">Data</Label><Input type="date" value={ex.data_exame} onChange={e => updateListItem("exames_saude", i, "data_exame", e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs">Resultado</Label><NativeSelect value={ex.resultado} onValueChange={v => updateListItem("exames_saude", i, "resultado", v)} options={[{ value: "", label: "Selecione..." }, { value: "apto", label: "Apto" }, { value: "inapto", label: "Inapto" }, { value: "apto_restricoes", label: "Apto com Restricoes" }]} /></div>
                      <div className="space-y-1"><Label className="text-xs">Proximo Exame</Label><Input type="date" value={ex.proximo_exame} onChange={e => updateListItem("exames_saude", i, "proximo_exame", e.target.value)} /></div>
                    </div>
                    <DocUpload docs={ex.docs || []} onAdd={e => handleNestedDocUpload(e, "exames_saude", i, "docs")} onRemove={di => removeNestedDoc("exames_saude", i, "docs", di)} label="Anexar laudo/ASO" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 pt-4 pb-8">
          <Link to="/cadastros/funcionarios"><Button type="button" variant="outline">Cancelar</Button></Link>
          <Button type="submit" disabled={saving} className="bg-[#0A1628] hover:bg-[#0A1628]/90" data-testid="btn-salvar">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvando...</> : isEdit ? "Atualizar" : "Cadastrar"}
          </Button>
        </div>
      </form>

      {/* Dialog Cargo */}
      <Dialog open={showCargoDialog} onOpenChange={setShowCargoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-[#0A1628]">Novo Cargo</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2"><Label>Nome *</Label><Input value={newCargo.nome} onChange={e => setNewCargo(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Eletricista" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Nivel</Label><Input type="number" min={1} max={10} value={newCargo.nivel} onChange={e => setNewCargo(p => ({ ...p, nivel: parseInt(e.target.value) || 1 }))} /></div>
              <div className="space-y-2"><Label>Salario Base (R$)</Label><Input type="number" step="0.01" value={newCargo.salario_base} onChange={e => setNewCargo(p => ({ ...p, salario_base: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCargoDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateCargo} className="bg-[#0A1628]">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Promocao */}
      <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-[#0A1628] flex items-center gap-2"><TrendingUp className="h-5 w-5 text-[#C9A84C]" />Registrar Promocao</DialogTitle><DialogDescription>Registre a promocao e atualizacao salarial do funcionario.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-slate-50 rounded text-sm">
              <p className="text-slate-500">Cargo atual: <strong>{form.cargo_nome || "Nenhum"}</strong></p>
              <p className="text-slate-500">Salario atual: <strong>{form.salario_inicial ? `R$ ${Number(form.salario_inicial).toFixed(2)}` : "-"}</strong></p>
            </div>
            <div className="space-y-2">
              <Label>Novo Cargo</Label>
              <NativeSelect value={promoData.novo_cargo_id} onValueChange={v => { const c = cargos.find(x => x.id === v); setPromoData(p => ({ ...p, novo_cargo_id: v, novo_cargo_nome: c?.nome || "", novo_salario: c?.salario_base ? String(c.salario_base) : p.novo_salario })); }} options={[{ value: "", label: "Selecione..." }, ...cargos.map(c => ({ value: c.id, label: `${c.nome} (Nv.${c.nivel})${c.salario_base ? ` - R$${Number(c.salario_base).toFixed(2)}` : ""}` }))]} />
            </div>
            <div className="space-y-2"><Label>Novo Salario (R$)</Label><Input type="number" step="0.01" value={promoData.novo_salario} onChange={e => setPromoData(p => ({ ...p, novo_salario: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Motivo</Label><Input value={promoData.motivo} onChange={e => setPromoData(p => ({ ...p, motivo: e.target.value }))} placeholder="Ex: Promocao por merito" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoDialog(false)}>Cancelar</Button>
            <Button onClick={handlePromotion} className="bg-[#0A1628]" disabled={!promoData.novo_cargo_id}>Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== VIEW ====================
function FuncionarioView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = sessionStorage.getItem("luvcorp_token");

  useEffect(() => {
    axios.get(`${API}/funcionarios/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { toast.error("Nao encontrado"); navigate("/cadastros/funcionarios"); });
  }, [id]);

  if (loading || !data) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const Info = ({ label, value }) => value ? <div><p className="text-xs text-slate-400">{label}</p><p className="text-sm font-medium">{value}</p></div> : null;
  const ecLabel = ESTADOS_CIVIS.find(e => e.value === data.estado_civil)?.label || data.estado_civil;
  const escLabel = ESCOLARIDADES.find(e => e.value === data.grau_escolaridade)?.label || data.grau_escolaridade;

  return (
    <div className="space-y-4" data-testid="funcionario-view">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/cadastros/funcionarios"><Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button></Link>
          <Avatar className="h-12 w-12">{data.foto ? <AvatarImage src={data.foto} /> : null}<AvatarFallback className="bg-[#0A1628] text-white">{data.nome?.split(" ").map(w => w[0]).join("").slice(0, 2)}</AvatarFallback></Avatar>
          <div><h1 className="text-xl font-bold text-[#0A1628]">{data.nome}</h1>{data.cargo_nome && <Badge variant="outline">{data.cargo_nome}</Badge>}</div>
        </div>
        <Link to={`/cadastros/funcionarios/${id}/edit`}><Button variant="outline" size="sm"><Edit className="h-4 w-4 mr-1" />Editar</Button></Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm"><CardContent className="p-4 space-y-2"><Info label="CPF" value={data.cpf} /><Info label="Estado Civil" value={ecLabel} /><Info label="Nascimento" value={data.data_nascimento ? new Date(data.data_nascimento).toLocaleDateString("pt-BR") : null} /></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4 space-y-2"><Info label="Admissao" value={data.data_admissao ? new Date(data.data_admissao).toLocaleDateString("pt-BR") : null} /><Info label="Salario" value={data.salario_inicial ? `R$ ${Number(data.salario_inicial).toFixed(2)}` : null} /><Info label="Carga Horaria" value={data.carga_horaria_semanal ? `${data.carga_horaria_semanal}h/sem` : null} /></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4 space-y-2">{data.telefone_contato && <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-slate-400" />{data.telefone_contato}</div>}{data.email && <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-slate-400" />{data.email}</div>}{data.endereco && <div className="flex items-center gap-2 text-sm mt-1"><MapPin className="h-4 w-4 text-slate-400" />{data.endereco}{data.numero ? `, ${data.numero}` : ""} - {data.cidade}/{data.estado}</div>}</CardContent></Card>
      </div>

      {(data.rg_numero || data.pis || data.titulo_eleitor_inscricao) && (
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Documentos</CardTitle></CardHeader><CardContent><div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Info label="RG" value={data.rg_numero ? `${data.rg_numero}${data.rg_orgao_emissor ? ` - ${data.rg_orgao_emissor}` : ""}` : null} />
          <Info label="CNH" value={data.cnh_numero ? `${data.cnh_numero}${data.cnh_categoria ? ` (${data.cnh_categoria})` : ""}` : null} />
          <Info label="PIS" value={data.pis} />
          <Info label="Titulo Eleitor" value={data.titulo_eleitor_inscricao ? `Insc: ${data.titulo_eleitor_inscricao} / Zona: ${data.titulo_eleitor_zona || "-"} / Secao: ${data.titulo_eleitor_secao || "-"}` : null} />
          <Info label="Reservista" value={data.certificado_reservista} />
        </div></CardContent></Card>
      )}

      {(data.filiacao_mae || data.filiacao_pai) && <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Filiacao</CardTitle></CardHeader><CardContent><div className="grid grid-cols-1 md:grid-cols-2 gap-4"><Info label="Mae" value={data.filiacao_mae} /><Info label="Pai" value={data.filiacao_pai} /></div></CardContent></Card>}

      {(data.filhos || []).length > 0 && (
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Dependentes</CardTitle></CardHeader><CardContent className="space-y-2">
          {data.filhos.map((f, i) => (<div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded"><Users className="h-4 w-4 text-slate-400" /><span className="text-sm font-medium">{f.nome}</span><span className="text-xs text-slate-500">{f.sexo === "masculino" ? "M" : f.sexo === "feminino" ? "F" : ""}</span>{f.data_nascimento && <span className="text-xs text-[#C9A84C]">{calcAge(f.data_nascimento)}</span>}</div>))}
        </CardContent></Card>
      )}

      {data.grau_escolaridade && <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Escolaridade</CardTitle></CardHeader><CardContent><p className="text-sm">{escLabel}</p></CardContent></Card>}

      {(data.dias_folga || []).length > 0 && <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Dias de Folga</CardTitle></CardHeader><CardContent><div className="flex gap-2 flex-wrap">{data.dias_folga.map(d => <Badge key={d} variant="outline" className="capitalize">{DIAS_SEMANA.find(x => x.value === d)?.label || d}</Badge>)}</div></CardContent></Card>}

      {(data.historico_promocoes || []).length > 0 && (
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Historico de Promocoes</CardTitle></CardHeader><CardContent className="space-y-2">
          {data.historico_promocoes.map((p, i) => (<div key={i} className="p-3 bg-emerald-50 rounded text-sm"><p className="font-medium">{p.data ? new Date(p.data).toLocaleDateString("pt-BR") : ""} - {p.motivo}</p><p className="text-slate-500">{p.cargo_anterior} → {p.cargo_novo}{p.salario_novo ? ` (R$ ${Number(p.salario_novo).toFixed(2)})` : ""}</p></div>))}
        </CardContent></Card>
      )}

      {(data.exames_saude || []).length > 0 && (
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base flex items-center gap-2"><Heart className="h-4 w-4 text-red-500" />Exames</CardTitle></CardHeader><CardContent className="space-y-2">
          {data.exames_saude.map((ex, i) => (<div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"><div><p className="font-medium">{TIPOS_EXAME.find(t => t.value === ex.tipo)?.label || ex.tipo}</p>{ex.data_exame && <p className="text-xs text-slate-500">{new Date(ex.data_exame).toLocaleDateString("pt-BR")}</p>}</div>{ex.resultado && <Badge className={ex.resultado === "apto" ? "bg-emerald-100 text-emerald-700" : ex.resultado === "inapto" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>{ex.resultado === "apto" ? "Apto" : ex.resultado === "inapto" ? "Inapto" : "Apto c/ Restricoes"}</Badge>}</div>))}
        </CardContent></Card>
      )}
    </div>
  );
}

// ==================== ROUTER ====================
export default function FuncionariosPage() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<FuncionariosList />} />
        <Route path="new" element={<FuncionarioForm />} />
        <Route path=":id" element={<FuncionarioView />} />
        <Route path=":id/edit" element={<FuncionarioForm />} />
      </Routes>
    </MainLayout>
  );
}
